<template>
  <app-page class="paypal-page" fill lean>
    <!-- Aufbau wie die übrigen Tabellenseiten (Kundenwunsch 13.10.2026: „jetzt bitte alle shop seiten überarbeiten“):
         leichter Titel, Aktionen mit Beschriftung (Handy: Icon-Knöpfe); links Monat und Suche, rechts der Filter statt
         der Filterzeile -->
    <template #actions>
      <template v-if="!handy">
        <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
        <app-button icon="download" @click="exportToExcel">Excel-Export</app-button>
      </template>
      <template v-else>
        <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
        <app-icon-button icon="download" label="Excel-Export" @click="exportToExcel" />
      </template>
    </template>
    <template #toolbar>
      <app-month-stepper v-model="month" />
      <app-search v-model="searchText" placeholder="In Zahlungen suchen" @input="onSearchInput" />
    </template>
    <template #toolbar-end>
      <sale-filter-menu
        :value="filters"
        :facets="facets"
        :result-count="resultCount"
        :description="filterTooltip"
        :segment-defs="filterSegments"
        :time-defs="filterTimes"
        :time-days="timeDays"
        :active-filters="activeFilterCount"
        range-label="Betrag"
        :result-nouns="resultNouns"
        :empty-value="emptyPaypalFilters"
        :align-right="!handy"
        @open="refreshFacets"
        @input="onFiltersInput"
      />
    </template>

    <!-- Nicht blockierender Fortschritt der PayPal-Anreicherung als Toast unten rechts (AppToast) -->
    <app-toast
      :value="enrichLoading"
      :timeout="0"
      icon="download"
      text="PayPal-Rückerstattungsdaten werden geladen …"
      :progress="enrichPercent"
      :indeterminate="enrichTotal === 0"
      :count="enrichTotal > 0 ? `${enrichDone} / ${enrichTotal} (${enrichPercent}\u00a0%)` : ''"
    />

    <!-- Dialog liegt über der Seite und nimmt keinen Platz im Inhalt ein -->
    <app-dialog v-model="refundDialog" persistent :max-width="600">
      <template #title
        >Zahlung zu Bestellung #{{ refundData ? refundData.order_id : "" }}
        zurückerstatten</template
      >
      <p v-if="refundErrorMsg" class="app-field__error refund-error" role="alert">
        <app-icon name="alert" :size="14" />{{ refundErrorMsg }}
      </p>
      <div class="app-form-field refund-amount">
        <label class="app-label" for="paypal-refund-amount">Betrag in €</label>
        <app-text-field
          id="paypal-refund-amount"
          v-model="refundAmount"
          type="number"
          step=".01"
          pattern="^\d*(\.\d{0,2})?$"
          :max="maxRefundAmount"
          :min="0"
        />
      </div>
      <template #actions>
        <app-button @click="refundDialog = false">Abbrechen</app-button>
        <app-button kind="danger" :loading="refundLoading" @click="refund"
          >Zahlung zurückerstatten ({{ refundAmountText }})</app-button
        >
      </template>
    </app-dialog>

    <!-- Tabelle füllt die Höhe über der Fußleiste (Handy: natürliche Höhe) -->
    <div class="app-grid-fill paypal-grid" :class="{ 'paypal-grid--auto': handy }">
      <dx-data-grid
        :ref="gridRefName"
        :height="gridHeight"
        :data-source="payments"
        :show-borders="false"
        :show-column-lines="false"
        :show-row-lines="true"
        :column-auto-width="true"
        :allow-column-reordering="!gridTouch"
        :allow-column-resizing="!gridTouch"
        :hover-state-enabled="!gridTouch"
        :scrolling="scrolling"
        :element-attr="gridAttr"
        :remote-operations="false"
        @toolbar-preparing="onToolbarPreparing"
        @content-ready="onContentReady"
      >
        <DxColumn
          data-field="order_id"
          caption="Bestellung"
          :calculate-cell-value="calcOrderId"
        />
        <DxColumn
          data-field="customer_name"
          caption="Kunde"
        />
        <DxColumn
          data-field="amount"
          caption="Betrag"
          data-type="number"
          format="######0.00"
        />
        <DxColumn
          data-field="refunded_amount"
          caption="Zurückerstattet"
          data-type="number"
          format="######0.00"
          :calculate-cell-value="calcRefunded"
        />
        <DxColumn
          data-field="currency"
          caption="Währung"
          :calculate-cell-value="calcCurrency"
        />
        <DxColumn
          data-field="status"
          caption="Status"
          :calculate-cell-value="calcStatus"
          cell-template="statusTemplate"
        />
        <DxColumn
          data-field="created"
          caption="Zahlungszeit"
          data-type="datetime"
          format="dd.MM.yyyy HH:mm:ss"
          :calculate-cell-value="calcCreated"
        />
        <DxColumn
          data-field="refund_date"
          caption="Rückerstattungszeit"
          data-type="datetime"
          format="dd.MM.yyyy HH:mm:ss"
          :calculate-cell-value="calcRefundDate"
        />
        <DxColumn data-field="paypal_capture_id" caption="Capture-ID" />
        <DxColumn type="buttons">
          <DxButton
            hint="Zahlung zurückerstatten"
            icon="undo"
            :visible="true"
            :on-click="openRefundDialog"
          />
        </DxColumn>

        <dx-search-panel :visible="false" />

        <dx-editing
          refresh-mode="full"
          :allow-adding="false"
          :allow-updating="false"
          :allow-deleting="false"
          mode="batch"
        />

        <!-- Seitenwahl und Blättern in der Fußleiste (AppGridFooter) -->
        <dx-pager :visible="false" />
        <dx-paging :page-size="10" />
        <dx-export :enabled="false" />

        <!-- Status als Text; bei API-Fehler Bernstein mit Warn-Icon und Fehlermeldung als Tooltip -->
        <template #statusTemplate="cell">
          <span class="paypal-status" :class="{ 'paypal-status--warn': cell.data.data.api_error }">
            <span>{{ cell.data.text }}</span>
            <v-tooltip v-if="cell.data.data.api_error" top content-class="app-tooltip">
              <template v-slot:activator="{ on }">
                <span
                  class="paypal-status__icon"
                  tabindex="0"
                  :aria-label="cell.data.data.error_message"
                  v-on="on"
                >
                  <app-icon name="alertTriangle" :size="16" />
                </span>
              </template>
              <span>{{ cell.data.data.error_message }}</span>
            </v-tooltip>
          </span>
        </template>
      </dx-data-grid>
    </div>
    <app-grid-footer ref="fuss" :page-sizes="pageSizes">
      <template #summary>
        <span>Summe <strong>{{ sumText("amount") }}</strong></span>
        <span class="app-sep" aria-hidden="true">·</span>
        <span>Zurückerstattet <strong>{{ sumText("refunded") }}</strong></span>
        <span class="app-sep" aria-hidden="true">·</span>
        <span v-if="narrowed"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }}</span>
        <span v-else><strong>{{ summaryCount }}</strong> {{ summaryNoun }}</span>
      </template>
    </app-grid-footer>
  </app-page>
</template>

<script>
import {
  DxDataGrid,
  DxColumn,
  DxButton,
  DxPaging,
  DxPager,
  DxEditing,
  DxSearchPanel,
  DxExport,
} from "devextreme-vue/data-grid";
import DataGridMixin from "@/mixins/DataGridMixin";
import axios from "axios";
import moment from "moment";
import { formatEuro, formatNumber, plural } from "@/utils/format";
import query from "devextreme/data/query";
import SaleFilterMenu from "@/components/sales/SaleFilterMenu";
import { emptyFilters, activeCount, buildPredicate, buildFacets, buildTimeDays, describeFilters, NO_VALUE_TEXT } from "@/components/sales/saleFilters";
import { PAYPAL_FILTER_CONFIG, PAYPAL_STATUS_TEXT } from "@/components/shop/shopFilters";

export default {
  components: {
    DxDataGrid,
    DxColumn,
    DxButton,
    DxPager,
    DxPaging,
    DxEditing,
    DxSearchPanel,
    DxExport,
    SaleFilterMenu,
  },
  mixins: [DataGridMixin],
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: AppPage fill wirkt dort nicht, das Grid wächst mit seinen Zeilen; sonst füllt es die Höhe
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    // Fußleiste: Anzahl nach Filter und Suche, „4 von 24 …“ solange eingeschränkt
    narrowed() {
      return this.summary.count !== this.summary.loaded;
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded) + " " + plural(this.summary.loaded, "Zahlung", "Zahlungen");
    },
    summaryNoun() {
      return plural(this.summary.count, "Zahlung", "Zahlungen");
    },
    activeFilterCount() {
      return activeCount(this.filters, PAYPAL_FILTER_CONFIG);
    },
    filterTooltip() {
      const parts = describeFilters(this.filters, this.filterLabel, 3, PAYPAL_FILTER_CONFIG);
      return parts.length ? parts.join(" · ") : null;
    },

    // Betrag im Knopf nach R6 („12,50 €“)
    refundAmountText() {
      return formatEuro(this.refundAmount);
    },
    enrichPercent() {
      return this.enrichTotal > 0
        ? Math.round((this.enrichDone / this.enrichTotal) * 100)
        : 0;
    },
  },
  watch: {
    month: function () {
      this.refreshDataGrid();
    },
    refundAmount: function (newRefundAmount) {
      if (newRefundAmount > this.maxRefundAmount) {
        this.refundAmount = this.maxRefundAmount;
      }
    },
  },
  data() {
    return {
      gridRefName: "paypalPaymentGrid",
      month: new Date().toISOString().substr(0, 7),
      searchText: "",
      // Filter-Panel (Kundenwunsch 13.10.2026): Zustand, Werte der Listen, Treffer nach Filter und Suche
      filters: emptyFilters(PAYPAL_FILTER_CONFIG),
      filterSegments: PAYPAL_FILTER_CONFIG.segments,
      filterTimes: PAYPAL_FILTER_CONFIG.times,
      timeDays: {},
      resultNouns: ["Zahlung", "Zahlungen"],
      facets: [],
      resultCount: null,
      summary: { count: 0, loaded: 0, sums: {} },
      gridAttr: {},
      refundDialog: false,
      refundErrorMsg: null,
      refundLoading: false,
      refundData: null,
      refundAmount: 0,
      maxRefundAmount: 0,
      // Full list of payments (plain array data source). Rows arrive from the
      // DB instantly with status 'LOADING' and are then enriched in chunks.
      payments: [],
      loading: false,
      // Non-blocking progress panel for the background PayPal enrichment.
      enrichLoading: false,
      enrichDone: 0,
      enrichTotal: 0,
    };
  },
  mounted() {
    this.loadPayments();
  },
  methods: {
    // ---------------------------------------------------------------- Filter-Panel (Kundenwunsch 13.10.2026)
    emptyPaypalFilters() {
      return emptyFilters(PAYPAL_FILTER_CONFIG);
    },
    filterLabel(listKey, key) {
      if (key === "") {
        return NO_VALUE_TEXT;
      }
      if (listKey === "status") {
        return PAYPAL_STATUS_TEXT[key] || key;
      }
      return key;
    },
    refreshFacets() {
      this.facets = Object.freeze(buildFacets(this._rows || [], this.filters, this.filterLabel, PAYPAL_FILTER_CONFIG));
      this.timeDays = Object.freeze(buildTimeDays(this._rows || [], PAYPAL_FILTER_CONFIG));
    },
    // Filter als Prüffunktion auf der Datenquelle wie bei Verkäufen (jedes Mal eine neue Funktion)
    onFiltersInput(filters) {
      this.filters = filters || this.emptyPaypalFilters();
      this.applyFilters();
      this.refreshFacets();
    },
    applyFilters() {
      const grid = this.$refs[this.gridRefName] && this.$refs[this.gridRefName].instance;
      if (grid) {
        const match = buildPredicate(this.filters, PAYPAL_FILTER_CONFIG);
        grid.filter(match ? [match, "=", true] : null);
      }
    },
    onContentReady(e) {
      const grid = e.component;
      const count = Math.max(Number(grid.totalCount()) || 0, 0);
      this.resultCount = count;
      const rows = this._rows || [];
      let visible = rows;
      const filter = grid.getCombinedFilter();
      if (filter && rows.length) {
        try {
          visible = query(rows).filter(filter).toArray();
        } catch (err) {
          visible = rows;
        }
      }
      const sums = {};
      sums.amount = visible.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
      sums.refunded = visible.reduce((sum, row) => sum + (Number(row.refunded_amount) || 0), 0);
      this.summary = { count, loaded: Math.max(rows.length, count), sums };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
    },
    sumText(key) {
      return formatEuro(this.summary.sums[key] || 0);
    },
    calcOrderId(row) {
      return `#${row.order_id}`;
    },
    calcRefunded(row) {
      return row.refunded_amount !== null && row.refunded_amount !== undefined
        ? row.refunded_amount
        : null;
    },
    calcCurrency(row) {
      return row.currency ? row.currency.toUpperCase() : null;
    },
    calcStatus(row) {
      switch (row.status) {
        case "LOADING":
          return "wird geladen…";
        case "COMPLETED":
          return "Erfolgreich";
        case "REFUNDED":
          return "Zurückerstattet";
        case "PARTIALLY_REFUNDED":
          return "Teilweise zurückerstattet";
        case "PENDING":
          return "Ausstehend";
        default:
          return row.status;
      }
    },
    calcCreated(row) {
      return row.created ? new Date(row.created) : null;
    },
    calcRefundDate(row) {
      return row.refund_date ? new Date(row.refund_date) : null;
    },
    // Load all payments for the month from the DB (instant, no PayPal calls),
    // then enrich every row with live PayPal details in small chunks across
    // several requests. This keeps each request well under the 29s API Gateway
    // timeout and PayPal's burst limit, while still giving the grid the full
    // dataset so filtering/search over status & refunds work correctly.
    async loadPayments() {
      const CHUNK_SIZE = 100; // captures per enrich request
      const CHUNK_CONCURRENCY = 2; // enrich requests in flight at once

      const grid = this.$refs[this.gridRefName]
        ? this.$refs[this.gridRefName].instance
        : null;
      if (this.loading) return;
      this.loading = true;
      this.enrichLoading = true;
      this.enrichDone = 0;
      this.enrichTotal = 0;

      // Repaint only the currently visible rows from the (mutated) data so the
      // user's page, filter and sorting stay untouched while they keep working.
      const repaintVisible = () => {
        if (!grid) return;
        const indexes = grid
          .getVisibleRows()
          .filter((r) => r.rowType === "data")
          .map((r) => r.rowIndex);
        if (indexes.length) grid.repaintRows(indexes);
      };

      try {
        // 1) Base rows from the DB (status 'LOADING'), grid visible immediately.
        const rows = (
          await axios.get(
            `${this.$store.state.baseUrl}paypalpayment?month=${moment(
              this.month + "-01"
            ).format("MM")}&year=${moment(this.month + "-01").format("YYYY")}`
          )
        ).data;
        this.payments = Array.isArray(rows) ? rows : [];
        // dieselbe Liste für Filter-Panel und Fußleiste (die Anreicherung ändert die Zeilen darin)
        this._rows = this.payments;
        this.refreshFacets();

        const byCapture = {};
        this.payments.forEach((p) => {
          if (p.paypal_capture_id) byCapture[p.paypal_capture_id] = p;
        });

        // 2) Build chunks of capture ids to enrich.
        const captureIds = Object.keys(byCapture);
        const chunks = [];
        for (let i = 0; i < captureIds.length; i += CHUNK_SIZE) {
          chunks.push(captureIds.slice(i, i + CHUNK_SIZE));
        }
        this.enrichTotal = captureIds.length;

        const enrichChunk = async (ids) => {
          try {
            const enriched = (
              await axios.get(
                `${this.$store.state.baseUrl}paypalpayment?capture_ids=${encodeURIComponent(
                  ids.join(",")
                )}`
              )
            ).data;
            (enriched || []).forEach((update) => {
              const row = byCapture[update.paypal_capture_id];
              if (row) Object.assign(row, update);
            });
          } catch (err) {
            console.error("PayPal enrichment chunk failed", err);
          } finally {
            this.enrichDone += ids.length;
            // Update already-rendered cells without resetting the grid view.
            repaintVisible();
          }
        };

        // 3) Run chunks with limited client-side concurrency.
        let next = 0;
        const workers = Array.from(
          { length: Math.min(CHUNK_CONCURRENCY, chunks.length) },
          async () => {
            while (next < chunks.length) {
              const idx = next++;
              await enrichChunk(chunks[idx]);
            }
          }
        );
        await Promise.all(workers);
        // Status, Rückerstattung und Zahlungszeit stehen erst jetzt fest: gesetzte Filter neu anwenden (seit 14.10.2026;
        // vorher blieb z. B. „Rückerstattung: Mit“ auf dem Stand vor der Anreicherung)
        this.refreshFacets();
        if (this.activeFilterCount) {
          this.applyFilters();
        }
      } catch (err) {
        console.error("Loading PayPal payments failed", err);
      } finally {
        this.enrichLoading = false;
        this.loading = false;
      }
    },
    openRefundDialog(e) {
      const data = e.row.data;
      this.refundDialog = true;
      this.refundLoading = false;
      this.refundErrorMsg = null;
      this.refundData = data;
      this.refundAmount = data.amount;
      const refundedAmount = data.refunded_amount || 0;
      this.maxRefundAmount = data.amount - refundedAmount;
    },
    async refund() {
      try {
        // Validation
        if (this.refundAmount <= 0) {
          this.refundErrorMsg = 'Betrag muss größer als 0 sein';
          return;
        }
        if (this.refundAmount > this.maxRefundAmount) {
          this.refundErrorMsg = `Betrag darf nicht größer als ${this.maxRefundAmount}€ sein`;
          return;
        }

        this.refundLoading = true;
        this.refundErrorMsg = null;

        const response = await axios.put(
          `${this.$store.state.baseUrl}paypalpayment`,
          {
            capture_id: this.refundData.paypal_capture_id,
            amount: this.refundAmount,
            currency: this.refundData.currency
          }
        );

        if (response.data.success) {
          this.refundDialog = false;
          this.refundData = null;
          this.refreshDataGrid();
        } else {
          this.refundErrorMsg = response.data.message;
        }
      } catch (e) {
        this.refundErrorMsg = e.response?.data?.message || e.message;
      } finally {
        this.refundLoading = false;
      }
    },
    refreshDataGrid() {
      this.loadPayments();
    },
    exportToExcel() {
      this.$refs[this.gridRefName].instance.exportToExcel(false);
    },
    onSearchInput() {
      this.$refs[this.gridRefName].instance.searchByText(this.searchText || "");
    },
    onToolbarPreparing(e) {
      // Remove all default toolbar items; die leere Leiste ganz ausblenden (sonst zweite Linie über dem Tabellenkopf)
      e.toolbarOptions.items = [];
      e.toolbarOptions.visible = false;
    },
  },
};
</script>

<style scoped>
/* Handy: Grid in natürlicher Höhe */
.paypal-grid.paypal-grid--auto {
  flex: none;
}
.refund-amount {
  max-width: 200px;
}
p.refund-error {
  margin: 0 0 12px;
}
.paypal-status {
  display: flex;
  align-items: center;
  gap: 8px;
}
.paypal-status--warn {
  color: var(--amber);
}
.paypal-status__icon {
  display: inline-flex;
  border-radius: var(--r-ctl);
  color: var(--amber);
  cursor: default;
}

/* Nicht blockierender Fortschritt der PayPal-Anreicherung: weiß mit Kante, ohne Schatten, Balken Navy; schwebt über der
   Seite, daher im Dunkelmodus die gehobene Fläche (--surface-raised) wie Menüs und Overlays */
</style>
